// The recovery drawer: instalment schedule, payments, and chasing.
//
// Payments and instalments are two different records of the same money and
// both are kept: `payments` is what arrived and when, `instalments` is what
// was agreed. Recording a payment therefore also advances the schedule, so
// the progress bar and the balance never disagree.

import React, { useEffect, useState } from 'react';
import {
  C, Pill, Panel, Row, MiniTable, NextAction, money0, date, Bar,
} from './kit';
import {
  useAction, Notice, ActionButton, ActionRow, RowEditor, Input, FieldRow,
  EditPanel, StatusAction, RemoveAction,
} from './actions';
import { DocumentsTab, ActivityTab } from './tabs';

const today = () => new Date().toISOString().slice(0, 10);
const iso = (v) => (v ? new Date(v).toISOString().slice(0, 10) : '');

export default function RecoveryDrawer({ row: r, tab, reload }) {
  const act = useAction(reload);
  const [plan, setPlan] = useState([]);
  const [pay, setPay] = useState({ amount: '', receivedAt: today(), reference: '' });

  // `plan` is an EDIT BUFFER for the schedule; the progress bar below reads
  // `r.instalments` straight from the server. Seeding this only on `r._id`
  // meant that after a payment — which reloads the record and flips the
  // earliest unpaid instalment to `paid` — the bar moved and the row did not,
  // because the id had not changed so the buffer was never reseeded. QA read
  // that as the payment not being recorded; it was recorded, and the row was
  // stale. Reseed whenever the server's copy actually changes.
  //
  // `updatedAt` rather than the array: it changes on exactly the saves that
  // matter and is one scalar, so this cannot loop on a new array identity.
  // A reload only follows a successful write, so no in-progress edit is lost
  // that the server has not already accepted.
  useEffect(() => {
    setPlan((r.instalments || []).map(i => ({ ...i, dueDate: iso(i.dueDate) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r._id, r.updatedAt]);

  const paidCount = (r.instalments || []).filter(i => i.status === 'paid').length;
  // `overdue` is derived by the server on read — an instalment past its due
  // date and unpaid. Nothing stores it, so nothing goes stale.
  const overdueCount = (r.instalments || []).filter(i => i.status === 'overdue').length;
  const outstanding = Number(r.outstanding) || 0;

  // ─── Payment History ───
  if (tab === 'Payment History') {
    return (
      <>
        <Notice error={act.error} note={act.note} />

        <Panel title='Payments received'>
          <MiniTable
            head={['Received', 'Amount', 'Reference']}
            rows={(r.payments || []).map(p => [
              date(p.receivedAt), money0(p.amount, p.currency || r.currency), p.reference || '—',
            ])}
            empty='Nothing received yet.'
          />
          <div style={{
            display: 'flex', justifyContent: 'space-between', marginTop: 11,
            paddingTop: 10, borderTop: `1px solid ${C.hair}`, fontSize: 13, fontWeight: 700,
          }}>
            <span style={{ color: C.muted }}>Outstanding</span>
            <span style={{ color: outstanding === 0 ? C.green : C.ink, fontVariantNumeric: 'tabular-nums' }}>
              {money0(r.outstanding, r.currency)}
            </span>
          </div>
        </Panel>

        <Panel title='Record a payment'>
          <div style={{ display: 'grid', gap: 9 }}>
            <FieldRow>
              <Input
                label={`Amount (${r.currency || ''})`}
                type='number'
                value={pay.amount}
                onChange={(v) => setPay(p => ({ ...p, amount: v }))}
              />
              <Input
                label='Date received'
                type='date'
                value={pay.receivedAt}
                onChange={(v) => setPay(p => ({ ...p, receivedAt: v }))}
              />
            </FieldRow>
            <Input
              label='Payment reference'
              value={pay.reference}
              onChange={(v) => setPay(p => ({ ...p, reference: v }))}
              placeholder='WIRE-REC-000002'
            />
            <ActionRow>
              <ActionButton
                busy={act.saving}
                disabled={pay.amount === ''}
                onClick={async () => {
                  const ok = await act.run({
                    url: `/reinsurance/recoveries/${r._id}/payments`,
                    body: {
                      amount: Number(pay.amount),
                      receivedAt: pay.receivedAt,
                      reference: pay.reference.trim(),
                      currency: r.currency,
                    },
                    success: 'Payment recorded — the balance and ageing have moved.',
                  });
                  if (ok) setPay({ amount: '', receivedAt: today(), reference: '' });
                }}
              >
                Record payment
              </ActionButton>
              {(r.instalments || []).some(i => i.status !== 'paid') && (
                <ActionButton
                  tone='quiet'
                  onClick={() => {
                    const next = (r.instalments || []).find(i => i.status !== 'paid');
                    if (next) setPay(p => ({ ...p, amount: String(next.amount ?? '') }));
                  }}
                >
                  Next instalment
                </ActionButton>
              )}
            </ActionRow>
          </div>
        </Panel>
      </>
    );
  }

  // ─── Documents ───
  if (tab === 'Documents') return <DocumentsTab record={r} />;

  // ─── Activity ───
  if (tab === 'Activity') return <ActivityTab resource='recoveries' id={r._id} />;


  // ─── Overview ───
  return (
    <>
      <Notice error={act.error} note={act.note} />

      <Panel title='Key information'>
        <Row label='Recovery reference' value={r.recoveryNumber} />
        <Row label='Cedant' value={r.cedantName} />
        <Row label='Counterparty' value={r.counterpartyName} />
        <Row label='Claim / event' value={r.claimEvent || r.claimNumber} />
        <Row label='Treaty' value={r.treatyNumber} />
        <Row label='Recoverable amount' value={money0(r.recoverable, r.currency)} />
        <Row label='Paid amount' value={money0(r.recovered, r.currency)} />
        <Row label='Outstanding balance' value={money0(r.outstanding, r.currency)} strong />
        <Row label='Due date' value={date(r.dueDate)} />
        <Row label='Assigned to' value={r.assignedToName} />
        <Row label='Status' value={<Pill status={r.status} size='sm' />} />
      </Panel>

      {/* ── The workings ──
          Where the recoverable came from, step by step. A number this size
          that cannot be explained is a number nobody will agree to pay. */}
      {(r.calculation?.steps || []).length > 0 && (
        <Panel title='How this was calculated' tone='quiet'>
          <MiniTable
            head={['Step', 'Amount']}
            rows={r.calculation.steps.map(s => [s.label, money0(s.amount, r.currency)])}
          />
        </Panel>
      )}

      <Panel title='Instalments & collections'>
        {/* Progress as a bar, not only a sentence, and an overdue count
            beside it — a schedule with two late instalments should not read
            the same as one that is simply not due yet. */}
        <div style={{ marginBottom: 10 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            fontSize: 12.5, color: C.muted, marginBottom: 6,
          }}>
            <span>{paidCount} of {(r.instalments || []).length} instalments paid</span>
            {overdueCount > 0 && (
              <span style={{ color: C.red, fontWeight: 700 }}>{overdueCount} overdue</span>
            )}
          </div>
          <Bar
            percent={(r.instalments || []).length
              ? Math.round((paidCount / r.instalments.length) * 100) : 0}
            tone={overdueCount > 0 ? 'red' : 'green'}
          />
        </div>
        <RowEditor
          columns={[
            { key: 'number', label: '#', numeric: true },
            { key: 'dueDate', label: 'Due date', placeholder: 'YYYY-MM-DD' },
            { key: 'amount', label: 'Amount', numeric: true },
            {
              key: 'status',
              label: 'Status',
              wide: true,
              options: [
                { value: 'pending', label: 'Pending' },
                { value: 'overdue', label: 'Overdue' },
                { value: 'paid', label: 'Paid' },
              ],
            },
          ]}
          rows={plan}
          onChange={setPlan}
          blank={{ number: plan.length + 1, dueDate: '', amount: '', status: 'pending' }}
          addLabel='+ Add instalment'
          saving={act.saving}
          total={{
            label: 'Scheduled',
            value: money0(plan.reduce((a, i) => a + (Number(i.amount) || 0), 0), r.currency),
            // The schedule must add up to what is recoverable, or the plan is
            // collecting a different debt from the one on the record.
            ok: Math.abs(
              plan.reduce((a, i) => a + (Number(i.amount) || 0), 0) - (Number(r.recoverable) || 0),
            ) < 1 || plan.length === 0,
            warning: 'The schedule does not add up to the recoverable amount.',
          }}
          onSave={() => act.run({
            method: 'patch',
            url: `/reinsurance/recoveries/${r._id}`,
            body: {
              instalments: plan.map((i, idx) => ({
                number: Number(i.number) || idx + 1,
                dueDate: i.dueDate || undefined,
                amount: Number(i.amount) || 0,
                status: i.status || 'pending',
              })),
            },
            success: 'Instalment schedule saved.',
          })}
        />
      </Panel>

      {r.counterpartyBank?.iban && (
        <Panel title='Counterparty bank details' tone='quiet'>
          <Row label='Bank' value={r.counterpartyBank.bankName} />
          <Row label='Account name' value={r.counterpartyBank.accountName} />
          <Row label='IBAN' value={r.counterpartyBank.iban} />
          <Row label='SWIFT' value={r.counterpartyBank.swift} />
        </Panel>
      )}

      {/* ── The record itself ──
          Correcting, moving and withdrawing a record, in one place on the
          Overview tab. Before this a screen could create a record and then
          never touch it again. Removal is a void or a cancel, never a
          delete — see reinsuranceShared.js. */}
      <Panel title='Record'>
        <div style={{ display: 'grid', gap: 10 }}>
          <EditPanel
            record={r}
            endpoint='/reinsurance/recoveries'
            onSaved={reload}
            fields={[
            { key: 'grossLoss', label: 'Gross loss', type: 'number' },
            { key: 'cededPercent', label: 'Ceded %', type: 'number' },
            { key: 'recoverable', label: 'Recoverable', type: 'number' },
            { key: 'dueDate', label: 'Due date', type: 'date' },
            { key: 'notes', label: 'Notes' },
            ]}
          />
          <StatusAction
            record={r}
            endpoint='/reinsurance/recoveries'
            onSaved={reload}
            options={[{ value: 'pending', label: 'Pending' }, { value: 'in_progress', label: 'In Progress' }, { value: 'overdue', label: 'Overdue' }, { value: 'settled', label: 'Settled' }, { value: 'disputed', label: 'Disputed' }, { value: 'written_off', label: 'Written Off' }]}
          />
          <RemoveAction record={r} endpoint='/reinsurance/recoveries' noun='recovery' onRemoved={reload} />
        </div>
      </Panel>

      <Panel title='Actions'>
        <ActionRow>
          <ActionButton
            tone='quiet'
            busy={act.saving}
            onClick={() => act.run({
              method: 'patch',
              url: `/reinsurance/recoveries/${r._id}`,
              body: { status: 'in_progress' },
              success: 'Marked in progress — the notice has gone out.',
            })}
          >
            Issue recovery notice
          </ActionButton>
          <ActionButton
            tone='quiet'
            busy={act.saving}
            onClick={() => act.run({
              url: `/reinsurance/recoveries/${r._id}/cash-call`,
              body: { amount: Math.abs(outstanding), currency: r.currency },
              success: 'Cash call raised.',
            })}
          >
            Raise cash call
          </ActionButton>
          <ActionButton
            tone='quiet'
            busy={act.saving}
            onClick={() => act.run({
              method: 'patch',
              url: `/reinsurance/recoveries/${r._id}`,
              body: { status: 'disputed' },
              success: 'Marked disputed.',
            })}
          >
            Mark disputed
          </ActionButton>
        </ActionRow>
      </Panel>

      {r.nextAction?.label && (
        <NextAction
          label={r.nextAction.label}
          detail={r.nextAction.detail}
          button='Record a payment'
        />
      )}
    </>
  );
}
